/**
 * 22. Trial Balance — each account's turnover and its balance on its side,
 * up to a date.
 *
 * A trial balance is one set of books, in its legal entity's functional
 * currency: every row carries it, and so does the list's meta with the
 * server's totals. When the workspace keeps books in more than one currency
 * the API answers 422 LEGAL_ENTITY_REQUIRED and the page asks which entity
 * (docs/design/currency.md R6, CUR-2).
 *
 * REP-FIELDS (D665, RT-D5): the screen reads exactly what GET
 * /v1/trial-balance sends. `debitTotal` and `creditTotal` are turnover, the
 * sums of the lines; `netDebit` and `netCredit` are the balance on its side.
 * Both pairs are shown under their own names, because labelling turnover as
 * a balance would show an untrue figure (1120: 103,000.00 of debits, a
 * 96,000.00 debit balance). Every total is the server's; the page adds
 * nothing up. The date is a real query parameter (it used to be two
 * hard-coded months that changed nothing). Opening balances and a period's
 * movement are not sent yet, and the page says so.
 *
 * Arabic and English (C7): every word from the catalogs; an account type is
 * the glossary's word.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  PageHeader,
  Skeleton,
  DataTable,
  Toolbar,
  Field,
  Input,
  Badge,
} from "@/components/ui";
import { PendingButton, usePendingReason } from "@/components/PendingAction";
import {
  LegalEntityPicker,
  legalEntitiesRequired,
  retryUnlessLegalEntityRequired,
  useOfferedLegalEntities,
  withLegalEntity,
} from "@/components/LegalEntityPicker";
import { QueryError } from "@/components/QueryError";
import { useApiList } from "@/lib/api/hooks";
import { withQuery } from "@/lib/api/use-cursor-list";
import { useAuthStore } from "@/lib/auth/store";
import { isZeroDecimal } from "@/lib/decimal-text";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { valueLabel } from "@/lib/i18n/records";
import { formatMoney } from "@/lib/money";

/** One row of GET /v1/trial-balance, exactly as the route sends it. */
interface TrialBalanceRow {
  accountId: string;
  code: string;
  name: string;
  accountType: string;
  /** Turnover: the sum of the debit lines up to the date. */
  debitTotal: string;
  /** Turnover: the sum of the credit lines up to the date. */
  creditTotal: string;
  /** The balance when it is a debit, else 0. */
  netDebit: string;
  /** The balance when it is a credit, else 0. */
  netCredit: string;
  currencyCode: string;
}

/** GET /v1/trial-balance `meta`: the server's exact totals and the books' currency. */
interface TrialBalanceMeta {
  totalDebit?: string;
  totalCredit?: string;
  totalNetDebit?: string;
  totalNetCredit?: string;
  balanced?: boolean;
  currencyCode?: string;
}

/** The slice that brings opening and movement columns (BUILD-PLAN AE-ST1). */
const OPENING_SLICE = "AE-ST1";

/**
 * A by-side amount: blank ("—") on the side the balance is not on. An
 * account whose balance is nil shows 0.00 on both sides, so it never reads
 * as missing.
 */
function sideAmount(value: string, other: string, currency: string): string {
  if (!isZeroDecimal(value)) return formatMoney(value, currency);
  return isZeroDecimal(other) ? formatMoney(value, currency) : "—";
}

/** A turnover amount: blank ("—") when nothing was posted on that side. */
function turnover(value: string, currency: string): string {
  return isZeroDecimal(value) ? "—" : formatMoney(value, currency);
}

export function TrialBalancePage() {
  const { t } = useTranslation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const openingWhy = usePendingReason("statementEngine", OPENING_SLICE);
  const [chosenEntity, setChosenEntity] = useState("");
  const [asOf, setAsOf] = useState("");
  const { data, isPending, error, refetch, isFetching } =
    useApiList<TrialBalanceRow>(
      asOf
        ? ["finance", "trial-balance", chosenEntity, asOf]
        : ["finance", "trial-balance", chosenEntity],
      withQuery(withLegalEntity("/finance/trial-balance", chosenEntity), {
        asOfDate: asOf,
      }),
      { retry: retryUnlessLegalEntityRequired },
    );
  const entities = useOfferedLegalEntities(error);
  const askingForEntity =
    (legalEntitiesRequired(error)?.length ?? 0) > 0 && !chosenEntity;

  const meta = (data?.meta ?? {}) as TrialBalanceMeta;
  // The books' currency, sent even when there are no rows. Were it missing,
  // the totals would show "¤" (R7), never a guessed currency.
  const currency = meta.currencyCode ?? "";
  const balanced = meta.balanced === true;
  const canOpenLedger = hasPermission("gl.read");
  const ledgerOf = (r: TrialBalanceRow) =>
    `/app/finance/general-ledger?accountId=${encodeURIComponent(r.accountId)}${
      asOf ? `&to=${encodeURIComponent(asOf)}` : ""
    }`;

  return (
    <div>
      <PageHeader
        title={t("finance.trialBalance.title")}
        subtitle={t("finance.trialBalance.subtitle")}
        actions={
          <PendingButton reason="statements" slice="AE-W3">
            {t("shared.export")}
          </PendingButton>
        }
      />
      <Toolbar>
        <Field
          label={t("finance.trialBalance.asOf")}
          hint={t("finance.trialBalance.asOfHint")}
        >
          <Input
            type="date"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </Field>
      </Toolbar>
      <LegalEntityPicker
        entities={entities}
        value={chosenEntity}
        onChange={setChosenEntity}
      />
      {isPending ? (
        <Skeleton rows={12} />
      ) : askingForEntity ? null : error || !data ? (
        <QueryError
          error={error}
          retrying={isFetching}
          onRetry={() => void refetch()}
        />
      ) : (
        <Card
          title={listTitle(t("finance.trialBalance.list"), data.items.length)}
          actions={
            <Badge tone={balanced ? "success" : "danger"}>
              {balanced
                ? t("finance.trialBalance.balanced")
                : t("finance.trialBalance.outOfBalance")}
            </Badge>
          }
        >
          <div role="status" style={{ marginBlockEnd: 12 }}>
            <p style={{ marginBlock: "0 4px" }}>
              {t("finance.trialBalance.openingNotShown")}
            </p>
            <p style={{ margin: 0 }}>{openingWhy}</p>
          </div>
          <DataTable<TrialBalanceRow>
            rows={data.items}
            keyField="accountId"
            columns={[
              {
                key: "code",
                label: t("fields.account"),
                render: (r) =>
                  canOpenLedger ? (
                    <Link className="atlas-mono" to={ledgerOf(r)}>
                      {r.code}
                    </Link>
                  ) : (
                    <span className="atlas-mono">{r.code}</span>
                  ),
              },
              { key: "name", label: t("fields.name") },
              {
                key: "accountType",
                label: t("fields.type"),
                render: (r) =>
                  r.accountType
                    ? valueLabel(t, "accountType", r.accountType)
                    : "—",
              },
              {
                key: "debitTotal",
                label: t("finance.trialBalance.debits"),
                numeric: true,
                render: (r) => turnover(r.debitTotal, r.currencyCode),
              },
              {
                key: "creditTotal",
                label: t("finance.trialBalance.credits"),
                numeric: true,
                render: (r) => turnover(r.creditTotal, r.currencyCode),
              },
              {
                key: "netDebit",
                label: t("finance.trialBalance.debitBalance"),
                numeric: true,
                render: (r) =>
                  sideAmount(r.netDebit, r.netCredit, r.currencyCode),
              },
              {
                key: "netCredit",
                label: t("finance.trialBalance.creditBalance"),
                numeric: true,
                render: (r) =>
                  sideAmount(r.netCredit, r.netDebit, r.currencyCode),
              },
            ]}
          />
          <div className="atlas-grid atlas-grid--2 mt-4">
            <Total
              label={t("finance.trialBalance.totalDebits")}
              value={formatMoney(meta.totalDebit, currency)}
            />
            <Total
              label={t("finance.trialBalance.totalCredits")}
              value={formatMoney(meta.totalCredit, currency)}
            />
            <Total
              label={t("finance.trialBalance.totalDebitBalances")}
              value={formatMoney(meta.totalNetDebit, currency)}
            />
            <Total
              label={t("finance.trialBalance.totalCreditBalances")}
              value={formatMoney(meta.totalNetCredit, currency)}
            />
          </div>
        </Card>
      )}
    </div>
  );
}

function Total({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <div className="atlas-stat__label">{label}</div>
      <div className="atlas-stat__value">{value}</div>
    </Card>
  );
}
